import { AxiosResponse } from 'axios';

import { ApiV2Response, Paginated } from '@dbt-labs/app-nav';

/**
 * In many places we need to navigate the user to a URL that is outside the scope of the React app.
 * Navigating away during component tests will cause the whole component test framework to be lost.
 * The solution to this problem so far has been to either:
 * 1. Leave the code untested
 * or
 * 2. Test the navigation in the last test in the suite
 *
 * The helper below provides a simple wrapper for updating `window.location.href` that can be easily
 * stubbed when running tests. To be able to allow the method to be stubbed, an object is exported
 * rather than the function itself. This allows tests to go:
 *     import externalNavigation from '@root/util/externalNavigation';
 *     cy.stub(externalNavigation, "navigateTo").as('navigateTo');
 *     ...
 *     cy.get('@navigateTo').should('have.been.calledWith', 'https://some.external.domain');
 */
export const externalNavigation = {
  navigateTo: (url: string) => {
    window.location.href = url;
  },
};

export const createV2Page = (
  response: AxiosResponse<ApiV2Response<any>>,
  pageSize: number,
): Paginated<any> => {
  const total = response.data.extra?.pagination.total_count ?? 0;
  const count = response.data.extra?.pagination.count ?? 0;
  const offset = response.data.extra?.filters.offset ?? 0;
  const hasNextPage = count + offset < total;
  const nextPageOffset = hasNextPage ? offset + pageSize : undefined;

  return { total, count, nextPageOffset, hasNextPage, data: response.data.data };
};

export const getFlattenedV3Pages = <T>(pages?: Paginated<T[]>[]): T[] => {
  return pages?.reduce((acc, curr) => acc.concat(curr.data), [] as T[]) ?? [];
};
